import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  CheckSquare, Search, Plus, Users, Shield, UserPlus,
  Bell, BarChart3, AlertTriangle, TrendingUp, Clock,
  Settings, ListTodo, Calendar, Filter, Mail, ChevronDown, Loader2,
  CheckCircle2, FolderKanban, Activity, CheckCheck, Inbox, ArrowUpRight, Sparkles
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { taskAPI, userAPI, notificationAPI, reportAPI } from '../api';
import { Modal, Avatar, StatusBadge, ProgressBar, Loader, EmptyState, Pagination } from '../App.core';
import { formatDate, formatRelative, ROLE_LABELS, ROLE_OPTIONS, isOverdue } from '../utils/helpers';

// =============================================
// MY TASKS PAGE
// =============================================

export const MyTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ status: '', priority: '', search: '', page: 1 });
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const navigate = useNavigate();

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 10 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await taskAPI.myTasks(params);
      setTasks(data.tasks);
      setPagination(data.pagination);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleStatusChange = async (taskId, newStatus) => {
    // 1. Optimistic update
    const prevTasks = [...tasks];
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, status: newStatus } : t)));
    setUpdatingTaskId(taskId);

    try {
      await taskAPI.update(taskId, { status: newStatus });
      toast.success('Status updated');
      loadTasks();
    } catch (err) {
      setTasks(prevTasks);
      toast.error(err.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const overdue = tasks.filter(t => t.is_overdue && t.status !== 'completed');
  const dueSoon = tasks.filter(t => !t.is_overdue && t.due_date && new Date(t.due_date) <= new Date(Date.now() + 7 * 86400000) && t.status !== 'completed');
  const completed = tasks.filter(t => t.status === 'completed');

  return (
    <>
      {/* Welcome Banner matching Projects & Dashboard design */}
      <div className="dashboard-welcome-banner">
        <div>
          <h2 className="dashboard-welcome-title">My Activities</h2>
          <div className="dashboard-welcome-subtitle">
            <span>Track, manage, and deliver your assigned construction site work items.</span>
          </div>
        </div>
      </div>

      {/* Modern KPI Cards Grid */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 24 }}>
        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Activities</span>
            <div className="kpi-card-icon-pill">
              <Activity size={16} />
            </div>
          </div>
          <div className="kpi-value">{pagination.total || tasks.length}</div>
          <div className="kpi-card-footer">
            <span>All assigned work items</span>
          </div>
        </div>

        <div className="kpi-card kpi-danger">
          <div className="kpi-card-header">
            <span className="kpi-label">Overdue</span>
            <div className="kpi-card-icon-pill">
              <AlertTriangle size={16} />
            </div>
          </div>
          <div className="kpi-value">{overdue.length}</div>
          <div className="kpi-card-footer">
            <span>Critical items past deadline</span>
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-card-header">
            <span className="kpi-label">Due This Week</span>
            <div className="kpi-card-icon-pill">
              <Clock size={16} />
            </div>
          </div>
          <div className="kpi-value">{dueSoon.length}</div>
          <div className="kpi-card-footer">
            <span>Upcoming programme gates</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Completed</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{completed.length}</div>
          <div className="kpi-card-footer">
            <span>Successfully closed out</span>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar matching Projects page */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={16} className="search-input-icon" />
          <input
            className="search-input"
            placeholder="Search activities by title or notes..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
            >
              <option value="">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="on_hold">On Hold</option>
              <option value="inspection">Inspection</option>
              <option value="rework">Rework</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>

          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.priority}
              onChange={e => setFilters(f => ({ ...f, priority: e.target.value, page: 1 }))}
            >
              <option value="">All Priorities</option>
              <option value="routine">Routine</option>
              <option value="standard">Standard</option>
              <option value="urgent">Urgent</option>
              <option value="safety_critical">Safety Critical</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {/* Activities Table Card */}
      {loading ? (
        <Loader />
      ) : (
        <div className="card">
          {tasks.length === 0 ? (
            <div style={{ padding: '20px 0' }}>
              <EmptyState
                icon={CheckSquare}
                title="No activities found"
                message="You're all caught up! When you are assigned activities, they will appear here."
              />
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Activity Title</th>
                    <th>Project Programme</th>
                    <th>Priority</th>
                    <th>Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map(t => {
                    const overdue = t.due_date && isOverdue(t.due_date);
                    return (
                      <tr
                        key={t.id}
                        className="clickable"
                        onClick={() => navigate(`/projects/${t.project_id}`)}
                      >
                        <td style={{ width: 160 }} onClick={e => e.stopPropagation()}>
                          <div className="task-status-dropdown-wrap">
                            <select
                              value={t.status}
                              onChange={e => handleStatusChange(t.id, e.target.value)}
                              disabled={updatingTaskId === t.id}
                              className={`task-status-dropdown status-${t.status}`}
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="on_hold">On Hold</option>
                              <option value="inspection">Inspection</option>
                              <option value="rework">Rework</option>
                              <option value="completed">Completed</option>
                            </select>
                            {updatingTaskId === t.id ? (
                              <Loader2 size={12} className="task-status-spinner" />
                            ) : (
                              <ChevronDown size={12} className="task-status-chevron" />
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, color: '#0B1220' }}>{t.title}</span>
                            {t.is_overdue && t.status !== 'completed' && (
                              <span title="Overdue item" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <AlertTriangle size={14} style={{ color: 'var(--px-danger)' }} />
                              </span>
                            )}
                          </div>
                          {t.description && (
                            <div style={{ fontSize: '0.76rem', color: 'var(--px-text-muted)', marginTop: 2, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.description}
                            </div>
                          )}
                        </td>
                        <td>
                          {t.project ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FolderKanban size={14} style={{ color: '#0038A7' }} />
                              <span style={{ fontWeight: 550, color: '#0B1220', fontSize: '0.85rem' }}>{t.project.name}</span>
                              {t.project.project_code && (
                                <span className="project-full-code" style={{ fontSize: '0.72rem', padding: '1px 5px' }}>{t.project.project_code}</span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                          )}
                        </td>
                        <td>
                          <StatusBadge status={t.priority} type="priority" />
                        </td>
                        <td>
                          {t.due_date ? (
                            <span className={`task-due-badge ${overdue && t.status !== 'completed' ? 'overdue' : ''}`}>
                              <Calendar size={12} />
                              <span>{formatDate(t.due_date)}</span>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && (pagination.total > 10 || pagination.total_pages > 1) && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        />
      )}
    </>
  );
};

// =============================================
// TEAM TASKS PAGE
// =============================================

export const TeamTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ status: '', priority: '', search: '', page: 1 });
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const navigate = useNavigate();

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 100 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await taskAPI.teamTasks(params);
      setTasks(data.tasks || []);
      setPagination(data.pagination || {});
    } catch (err) {
      toast.error('Failed to load site activities');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleStatusChange = async (taskId, newStatus) => {
    const prevTasks = [...tasks];
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, status: newStatus } : t)));
    setUpdatingTaskId(taskId);

    try {
      await taskAPI.update(taskId, { status: newStatus });
      toast.success('Status updated');
      loadTasks();
    } catch (err) {
      setTasks(prevTasks);
      toast.error(err.response?.data?.error || 'Failed to update activity status');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  // Group by assignee
  const grouped = tasks.reduce((acc, t) => {
    const key = t.assignee ? `${t.assignee.first_name} ${t.assignee.last_name}` : 'Unassigned';
    if (!acc[key]) acc[key] = { user: t.assignee, tasks: [] };
    acc[key].tasks.push(t);
    return acc;
  }, {});

  // High-level KPI metrics
  const activeActivities = tasks.filter(t => t.status === 'in_progress' || t.status === 'inspection' || t.status === 'rework');
  const overdueActivities = tasks.filter(t => (t.is_overdue || (t.due_date && isOverdue(t.due_date))) && t.status !== 'completed');
  const completedActivities = tasks.filter(t => t.status === 'completed');

  return (
    <>
      {/* Page Title & Subtitle */}
      <div className="dashboard-welcome-banner">
        <div>
          <h2 className="dashboard-welcome-title">Site Activities</h2>
          <div className="dashboard-welcome-subtitle">
            <span>Real-time multi-project contractor work schedule and team task distribution</span>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid (Dashboard & Projects style) */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-info">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Activities</span>
            <div className="kpi-card-icon-pill">
              <ListTodo size={16} />
            </div>
          </div>
          <div className="kpi-value">{tasks.length}</div>
          <div className="kpi-card-footer">
            <span>Across all assigned sites</span>
          </div>
        </div>

        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Active / In Progress</span>
            <div className="kpi-card-icon-pill">
              <Activity size={16} />
            </div>
          </div>
          <div className="kpi-value">{activeActivities.length}</div>
          <div className="kpi-card-footer">
            <span>Currently under execution</span>
          </div>
        </div>

        <div className="kpi-card kpi-danger">
          <div className="kpi-card-header">
            <span className="kpi-label">Overdue & Critical</span>
            <div className="kpi-card-icon-pill">
              <AlertTriangle size={16} />
            </div>
          </div>
          <div className="kpi-value">{overdueActivities.length}</div>
          <div className="kpi-card-footer">
            <span>Requires site attention</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Completed</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{completedActivities.length}</div>
          <div className="kpi-card-footer">
            <span>Signed-off & verified</span>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={16} className="search-input-icon" />
          <input
            className="search-input"
            placeholder="Search site activities by title or notes..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
            >
              <option value="">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="on_hold">On Hold</option>
              <option value="inspection">Inspection</option>
              <option value="rework">Rework</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>

          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.priority}
              onChange={e => setFilters(f => ({ ...f, priority: e.target.value, page: 1 }))}
            >
              <option value="">All Priorities</option>
              <option value="routine">Routine</option>
              <option value="standard">Standard</option>
              <option value="urgent">Urgent</option>
              <option value="safety_critical">Safety Critical</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <Loader />
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Site Activity Overview</div>
              <div className="card-subtitle">Contractor and team allocations across projects</div>
            </div>
          </div>
          <div style={{ padding: '20px 0' }}>
            <EmptyState
              icon={ListTodo}
              title="No site activities found"
              message="No site activities match your current filter criteria."
            />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Object.entries(grouped).map(([name, group]) => {
            const memberOverdue = group.tasks.filter(t => (t.is_overdue || (t.due_date && isOverdue(t.due_date))) && t.status !== 'completed').length;
            const memberInProgress = group.tasks.filter(t => t.status === 'in_progress').length;
            const memberCompleted = group.tasks.filter(t => t.status === 'completed').length;

            return (
              <div key={name} className="site-activity-card">
                {/* Assignee Card Header */}
                <div className="site-activity-header">
                  <div className="site-activity-user-info">
                    {group.user ? (
                      <Avatar firstName={group.user.first_name} lastName={group.user.last_name} />
                    ) : (
                      <div className="avatar" style={{ background: '#cbd5e1', color: '#475569', fontWeight: 600 }}>?</div>
                    )}
                    <div>
                      <div className="site-activity-user-title">{name}</div>
                      <div className="site-activity-user-sub">
                        <span>{group.tasks.length} total {group.tasks.length === 1 ? 'activity' : 'activities'}</span>
                        {group.user?.role && (
                          <>
                            <span>•</span>
                            <span>{ROLE_LABELS[group.user.role] || group.user.role}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Assignee Stat Badges */}
                  <div className="site-activity-stat-pills">
                    {memberInProgress > 0 && (
                      <span className="site-activity-stat-pill in-progress">
                        <Activity size={12} />
                        <span>{memberInProgress} in progress</span>
                      </span>
                    )}
                    {memberOverdue > 0 && (
                      <span className="site-activity-stat-pill overdue">
                        <AlertTriangle size={12} />
                        <span>{memberOverdue} overdue</span>
                      </span>
                    )}
                    {memberCompleted > 0 && (
                      <span className="site-activity-stat-pill completed">
                        <CheckCircle2 size={12} />
                        <span>{memberCompleted} completed</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Activity Rows */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {group.tasks.map(t => {
                    const overdue = (t.is_overdue || (t.due_date && isOverdue(t.due_date))) && t.status !== 'completed';
                    return (
                      <div
                        key={t.id}
                        className="site-activity-row"
                        onClick={() => navigate(`/projects/${t.project_id}`)}
                      >
                        {/* Status Select with Spinner */}
                        <div onClick={e => e.stopPropagation()} style={{ width: 145, flexShrink: 0 }}>
                          <div className="task-status-dropdown-wrap">
                            <select
                              value={t.status}
                              onChange={e => handleStatusChange(t.id, e.target.value)}
                              disabled={updatingTaskId === t.id}
                              className={`task-status-dropdown status-${t.status}`}
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="on_hold">On Hold</option>
                              <option value="inspection">Inspection</option>
                              <option value="rework">Rework</option>
                              <option value="completed">Completed</option>
                            </select>
                            {updatingTaskId === t.id ? (
                              <Loader2 size={12} className="task-status-spinner" />
                            ) : (
                              <ChevronDown size={12} className="task-status-chevron" />
                            )}
                          </div>
                        </div>

                        {/* Title & Project Info */}
                        <div className="site-activity-main-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className="site-activity-title">{t.title}</span>
                            {overdue && (
                              <span title="Overdue item" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <AlertTriangle size={14} style={{ color: 'var(--px-danger)' }} />
                              </span>
                            )}
                          </div>
                          {t.project && (
                            <div className="site-activity-project-tag">
                              <FolderKanban size={13} style={{ color: '#0038A7' }} />
                              <span style={{ fontWeight: 500, color: '#0B1220' }}>{t.project.name}</span>
                              {t.project.project_code && (
                                <span className="project-full-code" style={{ fontSize: '0.7rem', padding: '1px 5px' }}>
                                  {t.project.project_code}
                                </span>
                              )}
                              {t.milestone && (
                                <>
                                  <span style={{ color: 'var(--px-text-muted)' }}>•</span>
                                  <span style={{ color: 'var(--px-text-secondary)', fontSize: '0.75rem' }}>
                                    {t.milestone.name}
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Priority Badge & Due Date */}
                        <div className="site-activity-meta-right">
                          <StatusBadge status={t.priority} type="priority" />
                          {t.due_date ? (
                            <span className={`task-due-badge ${overdue ? 'overdue' : ''}`}>
                              <Calendar size={12} />
                              <span>{formatDate(t.due_date)}</span>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--px-text-muted)', fontSize: '0.8rem' }}>—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// =============================================
// ADMIN - USER MANAGEMENT
// =============================================

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', role: '' });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 100 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await userAPI.list(params);
      setUsers(data.users);
    } catch (err) { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await userAPI.deactivate(id); toast.success('User deactivated'); loadUsers(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleActivate = async (id) => {
    try { await userAPI.activate(id); toast.success('User activated'); loadUsers(); }
    catch (err) { toast.error('Failed'); }
  };

  const handleRoleChange = async (id, role) => {
    try { await userAPI.update(id, { role }); toast.success('Role updated'); loadUsers(); }
    catch (err) { toast.error('Failed'); }
  };

  const activeCount = users.filter(u => u.status === 'active').length;
  const invitedCount = users.filter(u => u.status === 'invited').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Top Header Card */}
      <div className="rfp-detail-top-card">
        <div className="rfp-detail-header-main">
          <div>
            <div className="rfp-detail-title-wrap">
              <h1 className="rfp-detail-title">User & Access Management</h1>
            </div>
            <div className="rfp-detail-submeta">
              <span className="rfp-detail-submeta-item">
                {users.length} Total Users ({activeCount} active, {invitedCount} pending invites)
              </span>
            </div>
          </div>

          <div className="rfp-detail-actions">
            <button className="btn btn-primary" onClick={() => setShowInvite(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <UserPlus size={15} /> Invite User
            </button>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar" style={{ margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', width: 320, flexShrink: 0 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--px-text-muted)' }} />
          <input
            className="search-input"
            placeholder="Search by user name or email..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            style={{ paddingLeft: 36, height: 38, fontSize: '0.84rem', width: '100%' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
          <div className="filter-select-wrapper" style={{ margin: 0, width: 160, flexShrink: 0 }}>
            <select
              className="filter-select"
              value={filters.role}
              onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}
              style={{ width: '100%', height: 38 }}
            >
              <option value="">All User Roles</option>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>

          <div className="filter-select-wrapper" style={{ margin: 0, width: 150, flexShrink: 0 }}>
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              style={{ width: '100%', height: 38 }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="deactivated">Deactivated</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {/* User Table Card */}
      {loading ? <Loader /> : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User Details</th>
                  <th>System Role</th>
                  <th>Account Status</th>
                  <th>Last Authentication</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 36, color: 'var(--px-text-muted)' }}>
                      No matching users found.
                    </td>
                  </tr>
                ) : (
                  users.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Avatar firstName={u.first_name} lastName={u.last_name} size="sm" />
                          <div>
                            <div style={{ fontWeight: 650, color: '#0b1220' }}>{u.first_name} {u.last_name}</div>
                            <div style={{ fontSize: '0.76rem', color: 'var(--px-text-muted)' }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <select
                          value={u.role}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                          style={{
                            padding: '5px 10px',
                            fontSize: '0.8rem',
                            borderRadius: 6,
                            border: '1px solid var(--px-border)',
                            background: '#f8fafc',
                            fontWeight: 550,
                            color: '#0b1220',
                            outline: 'none'
                          }}
                        >
                          {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            background:
                              u.status === 'active' ? '#d1fae5' :
                                u.status === 'invited' ? '#fef3c7' : '#fee2e2',
                            color:
                              u.status === 'active' ? '#059669' :
                                u.status === 'invited' ? '#b45309' : '#dc2626',
                            fontWeight: 650,
                            textTransform: 'uppercase',
                            fontSize: '0.72rem'
                          }}
                        >
                          {u.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--px-text-secondary)' }}>
                        {u.last_login ? formatRelative(u.last_login) : 'Never logged in'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {u.status === 'active' && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDeactivate(u.id)}
                            style={{ color: 'var(--px-danger)', borderColor: '#fecaca' }}
                          >
                            Deactivate
                          </button>
                        )}
                        {u.status === 'deactivated' && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleActivate(u.id)}
                            style={{ color: 'var(--px-success)', borderColor: '#a7f3d0' }}
                          >
                            Activate
                          </button>
                        )}
                        {u.status === 'invited' && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={async () => {
                              try {
                                await userAPI.resendInvite(u.id);
                                toast.success('Invite resent');
                              } catch {
                                toast.error('Failed to resend invite');
                              }
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                          >
                            <Mail size={13} /> Resend Invite
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showInvite && <InviteUserModal onClose={() => setShowInvite(false)} onSuccess={() => { setShowInvite(false); loadUsers(); }} />}
    </div>
  );
};

const InviteUserModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', role: 'field_engineer', title: '' });
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Email address is required.';
    if (!form.first_name.trim()) errs.first_name = 'First name is required.';
    if (!form.role) errs.role = 'Please select a system role.';
    return errs;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setSaving(true);
    try {
      await userAPI.invite(form);
      toast.success('Invitation sent successfully!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invitation.');
    } finally {
      setSaving(false);
    }
  };

  const set = (f) => (e) => {
    setFieldErrors(prev => {
      const copy = { ...prev };
      delete copy[f];
      return copy;
    });
    setForm(prev => ({ ...prev, [f]: e.target.value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-md"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <UserPlus size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Invite Team Member
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              Send an invitation with assigned project access permissions and role credentials
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ minWidth: 140 }}>
            {saving ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label className="form-label">Work Email Address <span className="required">*</span></label>
          <input
            className={`form-input ${fieldErrors.email ? 'has-error' : ''}`}
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="e.g. colleague@company.com"
          />
          {fieldErrors.email && (
            <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4 }}>
              {fieldErrors.email}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">First Name <span className="required">*</span></label>
            <input
              className={`form-input ${fieldErrors.first_name ? 'has-error' : ''}`}
              value={form.first_name}
              onChange={set('first_name')}
              placeholder="e.g. Tariq"
            />
            {fieldErrors.first_name && (
              <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4 }}>
                {fieldErrors.first_name}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Last Name</label>
            <input
              className="form-input"
              value={form.last_name}
              onChange={set('last_name')}
              placeholder="e.g. Al-Mansoor"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Assigned Role <span className="required">*</span></label>
            <div className="form-select-wrapper">
              <select className="form-select" value={form.role} onChange={set('role')}>
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <ChevronDown size={14} className="form-select-chevron" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Job Title</label>
            <input
              className="form-input"
              value={form.title}
              onChange={set('title')}
              placeholder="e.g. Senior Project Engineer"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

// =============================================
// REPORTS PAGE
// =============================================

export const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState('progress');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (activeReport === 'overdue') res = await reportAPI.overdue({ limit: 100 });
      else if (activeReport === 'workload') res = await reportAPI.workload({});
      else if (activeReport === 'progress') res = await reportAPI.progress({});
      else if (activeReport === 'completion') res = await reportAPI.completion({});
      setData(res.data);
    } catch (err) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [activeReport]);

  useEffect(() => { loadReport(); }, [loadReport]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Top Header Card */}
      <div className="rfp-detail-top-card">
        <div className="rfp-detail-header-main">
          <div>
            <div className="rfp-detail-title-wrap">
              <h1 className="rfp-detail-title">Project & Operations Reports</h1>
            </div>
            <div className="rfp-detail-submeta">
              <span className="rfp-detail-submeta-item">
                Comprehensive progress, milestone completion, and team resource reports
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Styled Tabs */}
      <div className="tabs" style={{ margin: 0, width: 'fit-content', alignSelf: 'flex-start' }}>
        {[
          { key: 'progress', label: 'Project Progress', icon: TrendingUp },
          { key: 'overdue', label: 'Overdue Milestones', icon: AlertTriangle },
          { key: 'workload', label: 'Resource Workload', icon: Users },
          { key: 'completion', label: 'Task Status Breakdown', icon: CheckSquare },
        ].map(tab => (
          <button
            key={tab.key}
            className={`tab ${activeReport === tab.key ? 'active' : ''}`}
            onClick={() => setActiveReport(tab.key)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <tab.icon size={15} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {loading ? <Loader /> : (
        <>
          {activeReport === 'progress' && data?.projects && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project Name & Code</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Lead Owner</th>
                      <th>Delivery Progress</th>
                      <th>Task Velocity</th>
                      <th>Scheduled Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projects.map(p => (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 650, color: '#0b1220' }}>{p.name}</div>
                          <div style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', fontFamily: 'var(--px-font-mono)', marginTop: 2 }}>
                            {p.project_code}
                          </div>
                        </td>
                        <td><StatusBadge status={p.status} /></td>
                        <td><StatusBadge status={p.priority} type="priority" /></td>
                        <td style={{ fontSize: '0.85rem' }}>{p.owner?.first_name} {p.owner?.last_name}</td>
                        <td style={{ minWidth: 120 }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 650, color: '#0b1220' }}>{p.progress}%</span>
                          <ProgressBar value={p.progress} />
                        </td>
                        <td style={{ fontSize: '0.82rem', fontWeight: 550 }}>
                          {p.task_stats?.completed}/{p.task_stats?.total}
                          {p.task_stats?.overdue > 0 && <span style={{ color: 'var(--px-danger)', fontWeight: 600 }}> ({p.task_stats.overdue} late)</span>}
                        </td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--px-text-secondary)' }}>
                          {formatDate(p.start_date)} &rarr; {formatDate(p.target_end_date)}
                          {p.is_behind_schedule && (
                            <span style={{ color: 'var(--px-danger)', fontWeight: 700, marginLeft: 6 }}>
                              &bull; DELAYED
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeReport === 'overdue' && data?.tasks && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {data.tasks.length === 0 ? (
                <div style={{ padding: 36 }}>
                  <EmptyState title="No overdue tasks" message="All project deliverables are on track!" />
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Overdue Task Title</th>
                        <th>Associated Project</th>
                        <th>Assigned Engineer</th>
                        <th>Target Due Date</th>
                        <th>Delay Duration</th>
                        <th>Priority Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tasks.map(t => (
                        <tr key={t.id}>
                          <td style={{ fontWeight: 600, color: '#0b1220' }}>{t.title}</td>
                          <td style={{ fontSize: '0.82rem', color: 'var(--px-text-secondary)' }}>{t.project?.name}</td>
                          <td>
                            {t.assignee ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Avatar firstName={t.assignee.first_name} lastName={t.assignee.last_name} size="sm" />
                                <span style={{ fontSize: '0.82rem', fontWeight: 500 }}>{t.assignee.first_name} {t.assignee.last_name}</span>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                            )}
                          </td>
                          <td style={{ color: 'var(--px-danger)', fontSize: '0.85rem', fontWeight: 600 }}>{formatDate(t.due_date)}</td>
                          <td>
                            <span style={{ color: 'var(--px-danger)', fontWeight: 700, background: '#fee2e2', padding: '2px 8px', borderRadius: 4, fontSize: '0.76rem' }}>
                              {t.days_overdue}d late
                            </span>
                          </td>
                          <td><StatusBadge status={t.priority} type="priority" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeReport === 'workload' && data?.workload && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {data.workload.map((w, i) => (
                <div
                  key={i}
                  className="card"
                  style={{
                    padding: 18,
                    borderRadius: 12,
                    border: '1px solid var(--px-border)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <Avatar firstName={w.assignee?.first_name} lastName={w.assignee?.last_name} size="sm" />
                    <div>
                      <div style={{ fontWeight: 650, color: '#0b1220' }}>{w.assignee?.first_name} {w.assignee?.last_name}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)' }}>{w.assignee?.title || 'Team Member'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ padding: '10px 8px', background: 'var(--px-bg)', borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0b1220' }}>{w.total_tasks}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--px-text-muted)', textTransform: 'uppercase' }}>Total Tasks</div>
                    </div>
                    <div style={{ padding: '10px 8px', background: '#fee2e2', borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--px-danger)' }}>{w.overdue_tasks || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: '#991b1b', textTransform: 'uppercase' }}>Overdue</div>
                    </div>
                    <div style={{ padding: '10px 8px', background: '#fef2f2', borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{w.critical_tasks || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: '#991b1b', textTransform: 'uppercase' }}>Critical</div>
                    </div>
                    <div style={{ padding: '10px 8px', background: '#fff7ed', borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#d97706' }}>{w.high_tasks || 0}</div>
                      <div style={{ fontSize: '0.72rem', color: '#9a3412', textTransform: 'uppercase' }}>High Priority</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeReport === 'completion' && data && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
              <div className="card" style={{ padding: '20px 22px' }}>
                <div className="card-header" style={{ marginBottom: 14, borderBottom: '1px solid var(--px-border)', paddingBottom: 10 }}>
                  <div className="card-title" style={{ fontWeight: 700 }}>Tasks by Lifecycle Status</div>
                </div>
                {(data.by_status || []).map(s => (
                  <div key={s.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--px-border)' }}>
                    <StatusBadge status={s.status} />
                    <span style={{ fontWeight: 750, fontSize: '1.15rem', color: '#0b1220' }}>{s.count}</span>
                  </div>
                ))}
              </div>
              <div className="card" style={{ padding: '20px 22px' }}>
                <div className="card-header" style={{ marginBottom: 14, borderBottom: '1px solid var(--px-border)', paddingBottom: 10 }}>
                  <div className="card-title" style={{ fontWeight: 700 }}>Tasks by Priority Tier</div>
                </div>
                {(data.by_priority || []).map(p => (
                  <div key={p.priority} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--px-border)' }}>
                    <StatusBadge status={p.priority} type="priority" />
                    <span style={{ fontWeight: 750, fontSize: '1.15rem', color: '#0b1220' }}>{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// =============================================
// NOTIFICATIONS PAGE
// =============================================

export const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filterTab, setFilterTab] = useState('all'); // 'all', 'unread', 'task', 'project'
  const [searchQuery, setSearchQuery] = useState('');
  const { fetchUnreadCount } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await notificationAPI.list({ limit: 50 });
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      } catch (err) {
        toast.error('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleMarkRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await notificationAPI.markRead(id);
      setNotifications(n => n.map(x => x.id === id ? { ...x, is_read: true } : x));
      setUnreadCount(c => Math.max(0, c - 1));
      fetchUnreadCount();
      toast.success('Marked as read');
    } catch (err) { }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(n => n.map(x => ({ ...x, is_read: true })));
      setUnreadCount(0);
      fetchUnreadCount();
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleClick = (notif) => {
    if (!notif.is_read) {
      handleMarkRead(notif.id);
    }
    if (notif.reference_type === 'task') {
      navigate('/my-tasks');
    } else if (notif.reference_type === 'project') {
      if (notif.reference_id) {
        navigate(`/projects/${notif.reference_id}`);
      } else {
        navigate('/projects');
      }
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filterTab === 'unread' && n.is_read) return false;
    if (filterTab === 'task' && n.reference_type !== 'task') return false;
    if (filterTab === 'project' && n.reference_type !== 'project') return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const titleMatch = (n.title || '').toLowerCase().includes(q);
      const msgMatch = (n.message || '').toLowerCase().includes(q);
      const actorMatch = n.actor ? `${n.actor.first_name} ${n.actor.last_name}`.toLowerCase().includes(q) : false;
      return titleMatch || msgMatch || actorMatch;
    }
    return true;
  });

  const getNotifIcon = (notif) => {
    if (notif.reference_type === 'task') {
      return (
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: '#eff6ff',
          color: '#2563eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #dbeafe',
          flexShrink: 0
        }}>
          <CheckSquare size={18} />
        </div>
      );
    }
    if (notif.reference_type === 'project') {
      return (
        <div style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: '#f0fdf4',
          color: '#16a34a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #bbf7d0',
          flexShrink: 0
        }}>
          <FolderKanban size={18} />
        </div>
      );
    }
    return (
      <div style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: '#f8fafc',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e2e8f0',
        flexShrink: 0
      }}>
        <Bell size={18} />
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', paddingBottom: 40 }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 24,
        paddingBottom: 20,
        borderBottom: '1px solid var(--px-border)'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--px-text)', margin: 0 }}>
              Notifications
            </h1>
            {unreadCount > 0 ? (
              <span style={{
                background: '#fee2e2',
                color: '#dc2626',
                fontWeight: 700,
                fontSize: '0.75rem',
                padding: '3px 10px',
                borderRadius: 20,
                border: '1px solid #fecaca',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />
                {unreadCount} unread
              </span>
            ) : (
              <span style={{
                background: '#f1f5f9',
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.75rem',
                padding: '3px 10px',
                borderRadius: 20,
                border: '1px solid #e2e8f0'
              }}>
                All caught up
              </span>
            )}
          </div>
          <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', color: 'var(--px-text-secondary)' }}>
            Stay updated on site task assignments, deadlines, project updates, and team mentions.
          </p>
        </div>

        {unreadCount > 0 && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleMarkAllRead}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontWeight: 600,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <CheckCheck size={16} />
            Mark all as read
          </button>
        )}
      </div>

      {/* Control Bar: Filters & Search */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 20
      }}>
        {/* Filter Tabs */}
        <div style={{
          display: 'inline-flex',
          background: '#f1f5f9',
          padding: 3,
          borderRadius: 10,
          border: '1px solid var(--px-border)'
        }}>
          {[
            { id: 'all', label: 'All', count: notifications.length },
            { id: 'unread', label: 'Unread', count: unreadCount },
            { id: 'task', label: 'Tasks' },
            { id: 'project', label: 'Projects' }
          ].map(tab => {
            const isActive = filterTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setFilterTab(tab.id)}
                style={{
                  border: 'none',
                  background: isActive ? '#ffffff' : 'transparent',
                  color: isActive ? '#0f172a' : 'var(--px-text-secondary)',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '0.82rem',
                  padding: '6px 14px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '1px 6px',
                    borderRadius: 10,
                    background: isActive ? (tab.id === 'unread' ? '#fee2e2' : '#f1f5f9') : '#e2e8f0',
                    color: tab.id === 'unread' ? '#dc2626' : 'var(--px-text-secondary)',
                    fontWeight: 700
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search Filter */}
        <div style={{ position: 'relative', width: 260 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--px-text-muted)' }} />
          <input
            type="text"
            className="input-field"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              paddingLeft: 32,
              paddingRight: 10,
              paddingTop: 7,
              paddingBottom: 7,
              fontSize: '0.82rem',
              borderRadius: 8,
              width: '100%',
              height: 36
            }}
          />
        </div>
      </div>

      {/* Notifications List */}
      {loading ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}>
          <Loader />
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="card" style={{
          padding: '48px 24px',
          textAlign: 'center',
          borderRadius: 14,
          background: '#ffffff',
          border: '1px solid var(--px-border)'
        }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: '#eff6ff',
            color: '#2563eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            border: '1px solid #dbeafe'
          }}>
            <Inbox size={26} />
          </div>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600, margin: '0 0 6px 0', color: 'var(--px-text)' }}>
            {searchQuery ? 'No matching notifications' : filterTab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </h3>
          <p style={{ fontSize: '0.84rem', color: 'var(--px-text-muted)', margin: 0, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            {searchQuery ? `We couldn't find any notification matching "${searchQuery}".` : "You're all caught up! New alerts and updates will appear here."}
          </p>
        </div>
      ) : (
        <div style={{
          background: '#ffffff',
          borderRadius: 14,
          border: '1px solid var(--px-border)',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          {filteredNotifications.map((n, idx) => {
            const isLast = idx === filteredNotifications.length - 1;
            return (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '16px 20px',
                  borderBottom: isLast ? 'none' : '1px solid var(--px-border)',
                  cursor: 'pointer',
                  background: n.is_read ? '#ffffff' : 'linear-gradient(to right, #f8faff 0%, #ffffff 100%)',
                  transition: 'background 0.15s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = n.is_read ? '#f8fafc' : '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = n.is_read ? '#ffffff' : 'linear-gradient(to right, #f8faff 0%, #ffffff 100%)';
                }}
              >
                {/* Left side: Notification Type Icon / Avatar */}
                {n.actor ? (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar firstName={n.actor.first_name} lastName={n.actor.last_name} size="avatar-sm" />
                    <span style={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      background: '#ffffff',
                      borderRadius: '50%',
                      padding: 2,
                      display: 'flex',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      {n.reference_type === 'task' ? (
                        <CheckSquare size={11} color="#2563eb" />
                      ) : (
                        <FolderKanban size={11} color="#16a34a" />
                      )}
                    </span>
                  </div>
                ) : (
                  getNotifIcon(n)
                )}

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginBottom: 3
                  }}>
                    <div style={{
                      fontWeight: n.is_read ? 500 : 700,
                      fontSize: '0.88rem',
                      color: n.is_read ? 'var(--px-text)' : '#0f172a'
                    }}>
                      {n.title}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexShrink: 0
                    }}>
                      <span style={{
                        fontSize: '0.74rem',
                        color: 'var(--px-text-muted)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <Clock size={12} />
                        {formatRelative(n.created_at)}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    fontSize: '0.83rem',
                    color: 'var(--px-text-secondary)',
                    lineHeight: 1.45,
                    marginBottom: 6
                  }}>
                    {n.message}
                  </div>

                  {/* Metadata / Tags */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {n.reference_type && (
                      <span style={{
                        fontSize: '0.7rem',
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        color: n.reference_type === 'task' ? '#2563eb' : '#16a34a',
                        background: n.reference_type === 'task' ? '#eff6ff' : '#f0fdf4',
                        padding: '2px 8px',
                        borderRadius: 6,
                        border: n.reference_type === 'task' ? '1px solid #dbeafe' : '1px solid #bbf7d0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        {n.reference_type === 'task' ? <CheckSquare size={11} /> : <FolderKanban size={11} />}
                        {n.reference_type}
                      </span>
                    )}
                    {n.actor && (
                      <span style={{ fontSize: '0.72rem', color: 'var(--px-text-muted)' }}>
                        by {n.actor.first_name} {n.actor.last_name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right Action: Mark as Read & Open link */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  alignSelf: 'center',
                  flexShrink: 0
                }}>
                  {!n.is_read ? (
                    <button
                      onClick={(e) => handleMarkRead(n.id, e)}
                      title="Mark as read"
                      style={{
                        background: '#ffffff',
                        border: '1px solid var(--px-border)',
                        color: 'var(--px-primary)',
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#eff6ff';
                        e.currentTarget.style.borderColor = '#bfdbfe';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = 'var(--px-border)';
                      }}
                    >
                      <CheckCheck size={14} />
                      Read
                    </button>
                  ) : (
                    <div style={{ color: 'var(--px-text-muted)', display: 'flex', alignItems: 'center' }}>
                      <ArrowUpRight size={16} />
                    </div>
                  )}

                  {!n.is_read && (
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--px-primary)',
                        flexShrink: 0,
                        boxShadow: '0 0 0 3px #dbeafe'
                      }}
                      title="Unread"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// =============================================
// PROFILE / SETTINGS PAGE
// =============================================

export const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || '', last_name: user?.last_name || '',
    title: user?.title || '', phone: user?.phone || '', timezone: user?.timezone || 'Asia/Kolkata',
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await userAPI.updateProfile(form);
      updateUser(form);
      toast.success('Profile updated');
    } catch (err) { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Passwords do not match'); return; }
    if (passwordForm.new_password.length < 8) { toast.error('Min 8 characters'); return; }
    try {
      await userAPI.changePassword({ current_password: passwordForm.current_password, new_password: passwordForm.new_password });
      toast.success('Password changed');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  return (
    <>
      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 20 }}><Settings size={22} style={{ verticalAlign: -3, marginRight: 8 }} />Profile & Settings</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Personal Information</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <Avatar firstName={user?.first_name} lastName={user?.last_name} size="avatar-lg" />
            <div>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>{user?.first_name} {user?.last_name}</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>{user?.email}</div>
              <span className="badge badge-active">{ROLE_LABELS[user?.role]}</span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">First Name</label>
              <input className="form-input" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Last Name</label>
              <input className="form-input" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
          </div>
          <div className="form-group"><label className="form-label">Timezone</label>
            <select className="form-select" value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Asia/Dubai">Asia/Dubai (GST)</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Change Password</div>
          <div className="form-group"><label className="form-label">Current Password</label>
            <input className="form-input" type="password" value={passwordForm.current_password} onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">New Password</label>
            <input className="form-input" type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Confirm New Password</label>
            <input className="form-input" type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} /></div>
          <button className="btn btn-primary" onClick={handleChangePassword}>Update Password</button>
        </div>
      </div>
    </>
  );
};
